import { motion } from "motion/react";
import { Link } from "react-router-dom";
import { ArrowRight, Github, Linkedin, Mail, Smartphone } from "lucide-react";
import { useTypewriter } from "@/hooks/use-typewriter.ts";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { SEOHead } from "@/components/seo-head.tsx";
import PageTransition from "@/components/page-transition.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Card } from "@/components/ui/card.tsx";

export default function Home() {
  const isMobile = useIsMobile();
  const homeSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Irfan Quttab",
    "alternateName": "Irfan Qutab",
    "title": "React Native Developer",
    "description": "Expert React Native developer specializing in cross-platform mobile applications for iOS and Android. Proficient in TypeScript, REST APIs, GraphQL, Firebase, and modern mobile development practices.",
    "url": "https://irfanqutab.dev",
    "image": "https://irfanqutab.dev/avatar.jpg",
    "email": "irfanquttab@gmail.com",
    "telephone": "+92-330-694-9000",
    "sameAs": [
      "https://github.com/irfanquttab1",
      "https://linkedin.com/in/irfanquttab",
      "https://twitter.com/irfanquttab"
    ],
    "jobTitle": "React Native Developer",
    "worksFor": {
      "@type": "Organization",
      "name": "BJS Soft Solutions",
      "url": "https://irfanqutab.dev"
    },
    "knowsAbout": [
      "React Native",
      "TypeScript",
      "JavaScript",
      "Mobile Development",
      "iOS Development",
      "Android Development",
      "Cross-Platform Development",
      "REST APIs",
      "GraphQL",
      "Firebase",
      "Redux Toolkit",
      "Socket.IO",
      "Mobile App Architecture",
      "Performance Optimization",
      "Electron.js"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "React Native Development Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "React Native App Development",
            "description": "End-to-end mobile app delivery for iOS and Android."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Performance Optimization",
            "description": "Improve app speed, stability, and memory usage."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "API & Real-time Integrations",
            "description": "GraphQL, REST, authentication, and Socket.IO integration."
          }
        }
      ]
    },
    "mainEntity": {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Do you build production-ready React Native apps?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. I design scalable architectures, integrate secure APIs, and deliver app store-ready builds with ongoing support."
          }
        },
        {
          "@type": "Question",
          "name": "Can you optimize an existing mobile app?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. I audit performance, reduce crashes, improve launch time, and modernize React Native versions safely."
          }
        }
      ]
    }
  };

  const { displayedText: title } = useTypewriter(
    "React Native Developer",
    100
  );
  const { displayedText: subtitle, isComplete } = useTypewriter(
    "Expert in Mobile Apps (iOS & Android) | RTK | REST & GraphQL APIs",
    50,
    2000
  );

  return (
    <PageTransition>
      <SEOHead
        title="React Native Developer | Mobile Apps (iOS/Android) - Irfan Qutab"
        description="Expert React Native developer specializing in iOS & Android apps. Professional in TypeScript, REST APIs, GraphQL, Firebase & Redux Toolkit."
        ogImage="/og-home.jpg"
        schema={homeSchema}
      />
      <main className="min-h-screen py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              initial={isMobile ? false : { opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={isMobile ? { duration: 0 } : { duration: 0.8, delay: 0.2 }}
              className="space-y-6"
            >
              <div className="space-y-2">
                <motion.p
                  initial={isMobile ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={isMobile ? { duration: 0 } : { delay: 0.5 }}
                  className="text-primary font-mono text-sm sm:text-base"
                >
                  {"<Hello World />"}
                </motion.p>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight break-words">
                  Irfan Qutab - React Native Developer & Mobile App Expert
                </h1>
                <p className="text-lg sm:text-xl text-muted-foreground min-h-[3rem] font-semibold">
                  Expert in iOS, Android & Desktop Apps | TypeScript | GraphQL | Firebase
                  {!isComplete && (
                    <motion.span
                      animate={{ opacity: [1, 0] }}
                      transition={{ duration: 0.5, repeat: Number.POSITIVE_INFINITY, repeatDelay: 0.5 }}
                      className="text-accent"
                    >
                      |
                    </motion.span>
                  )}
                </p>
              </div>

              <motion.p
                initial={isMobile ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={isMobile ? { duration: 0 } : { delay: 0.6 }}
                className="text-muted-foreground text-base sm:text-lg max-w-xl"
              >
                I specialize in crafting performant, scalable mobile applications
                using React Native, TypeScript, and modern development practices.
                Let's build something amazing together.
              </motion.p>

              <motion.div
                initial={isMobile ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={isMobile ? { duration: 0 } : { delay: 0.8 }}
                className="flex flex-wrap gap-4"
              >
                <Link to="/projects" className="block">
                  <Button size="lg" className="glow-primary group">
                    View My Work
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/contact" className="block">
                  <Button size="lg" variant="outline">
                    Get In Touch
                  </Button>
                </Link>
                <Link to="/skills" className="block">
                  <Button size="lg" variant="ghost">
                    My Skills
                  </Button>
                </Link>
              </motion.div>

              <motion.div
                initial={isMobile ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={isMobile ? { duration: 0 } : { delay: 1 }}
                className="pt-4"
              >
                <p className="text-sm font-semibold text-muted-foreground mb-3">Connect with me</p>
                <div className="flex gap-4">
                  <a
                    href="https://github.com/irfanquttab1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                    aria-label="GitHub"
                  >
                    <Github className="w-6 h-6" />
                  </a>
                  <a
                    href="https://www.linkedin.com/in/irfanquttab"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-6 h-6" />
                  </a>
                  <a
                    href="mailto:irfanquttab@gmail.com"
                    className="text-muted-foreground hover:text-primary transition-colors"
                    aria-label="Email"
                  >
                    <Mail className="w-6 h-6" />
                  </a>
                </div>
              </motion.div>
            </motion.div>

            {/* Visual Element */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="relative hidden lg:block"
            >
              <div className="relative w-full aspect-square max-w-md mx-auto">
                {/* Animated circles */}
                <motion.div
                  animate={{
                    rotate: 360,
                  }}
                  transition={{
                    duration: 20,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "linear",
                  }}
                  className="absolute inset-0 border-2 border-primary/20 rounded-full"
                />
                <motion.div
                  animate={{
                    rotate: -360,
                  }}
                  transition={{
                    duration: 15,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "linear",
                  }}
                  className="absolute inset-8 border-2 border-accent/20 rounded-full"
                />
                <motion.div
                  animate={{
                    rotate: 360,
                  }}
                  transition={{
                    duration: 10,
                    repeat: Number.POSITIVE_INFINITY,
                    ease: "linear",
                  }}
                  className="absolute inset-16 border-2 border-primary/30 rounded-full"
                />
                
                {/* Center profile image */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    animate={{
                      scale: [1, 1.05, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                    className="bg-card p-2 rounded-full border-2 border-primary glow-primary overflow-hidden"
                  >
                    <img 
                      src="https://images.unsplash.com/photo-1551650975-87deedd944c3?w=400&h=400&fit=crop&auto=format&q=75&fm=webp" 
                      alt="Irfan Qutab - React Native Developer working on mobile applications for iOS and Android"
                      className="w-48 h-48 rounded-full object-cover"
                      crossOrigin="anonymous"
                      width="192"
                      height="192"
                      loading="eager"
                      fetchPriority="high"
                      decoding="async"
                    />
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* SEO-friendly content section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="mt-24 max-w-6xl mx-auto"
            style={{ contentVisibility: "auto", containIntrinsicSize: "800px" }}
          >
            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
              <Card className="p-6 hover:border-primary/50 transition-colors">
                <h2 className="text-lg font-semibold mb-3 text-primary">React Native Development</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Professional <Link to="/projects" className="text-primary underline hover:no-underline">React Native developer</Link> building production-ready iOS and Android apps with TypeScript and modern cross-platform tooling.
                </p>
              </Card>
              
              <Card className="p-6 hover:border-primary/50 transition-colors">
                <h2 className="text-lg font-semibold mb-3 text-primary">Mobile App Architecture</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Specialized in scalable <Link to="/skills" className="text-primary underline hover:no-underline">mobile app architecture</Link> with Redux Toolkit, GraphQL, REST APIs, and Firebase for maintainable, fast apps.
                </p>
              </Card>
              
              <Card className="p-6 hover:border-primary/50 transition-colors">
                <h2 className="text-lg font-semibold mb-3 text-primary">Freelance Services</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Available for <Link to="/services" className="text-primary underline hover:no-underline">React Native services</Link> from planning and architecture to App Store and Play Store delivery.
                </p>
              </Card>

              <Card className="p-6 hover:border-primary/50 transition-colors">
                <h2 className="text-lg font-semibold mb-3 text-primary">Service Area</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  In-person in Lahore and remote worldwide, with clear milestones and reliable delivery.
                </p>
              </Card>
            </div>
            <div className="mt-8 text-sm text-muted-foreground">
              <p className="mb-2 font-medium text-foreground">Helpful Guides</p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <Link
                  to="/blog/hire-react-native-developer-pakistan-2026"
                  className="text-primary underline hover:no-underline"
                >
                  Hiring a React Native developer (2026)
                </Link>
                <Link
                  to="/blog/react-native-app-cost-2026"
                  className="text-primary underline hover:no-underline"
                >
                  React Native app cost
                </Link>
              </div>
            </div>
          </motion.section>
        </div>
      </main>
    </PageTransition>
  );
}
